import { Badge, Hint, Spacer } from '@ui-kit'
import React, { Fragment, useMemo } from 'react'
import { Trans } from 'react-i18next'
import { Column } from 'simple-flexbox'

import { useWizardContext } from '../contexts'
import useSteps from '../helpers/useSteps'
import { SectionPreview } from './SectionPreview'

interface SecondColumnContentProps {}

const NAME = 'NAME'
const GOAL = 'GOAL'
const STYLE = 'STYLE'
const COLOR_AND_FONT = 'COLOR_AND_FONT'
const TOPIC = 'TOPIC'
const CONTENT_GENERATION = 'CONTENT_GENERATION'

const hints = {
  [NAME]: [
    'wizard.second.column.name.hint.1',
    'wizard.second.column.name.hint.2',
    'wizard.second.column.name.hint.3'
  ],
  [GOAL]: [
    'wizard.second.column.goal.hint.1',
    'wizard.second.column.goal.hint.2',
    'wizard.second.column.goal.hint.3'
  ],
  [STYLE]: [
    'wizard.second.column.style.hint.1',
    'wizard.second.column.style.hint.2',
    'wizard.second.column.style.hint.3'
  ],
  [COLOR_AND_FONT]: [
    'wizard.second.column.color_and_font.hint.1',
    'wizard.second.column.color_and_font.hint.2',
    'wizard.second.column.color_and_font.hint.3'
  ],
  [TOPIC]: [
    'wizard.second.column.topic.hint.1',
    'wizard.second.column.topic.hint.2',
    'wizard.second.column.topic.hint.3'
  ],
  [CONTENT_GENERATION]: [
    'wizard.second.column.content.generation.hint.1',
    'wizard.second.column.content.generation.hint.2',
    'wizard.second.column.content.generation.hint.3'
  ]
} as const

const hintNames = {
  1: NAME,
  2: GOAL,
  3: GOAL,
  4: STYLE,
  5: COLOR_AND_FONT,
  6: TOPIC,
  8: CONTENT_GENERATION
} as const

const stepsWithoutPreview = [8, 9]

const getHint = (stepNumber: number, contentTone?: string) => {
  const name = hintNames[stepNumber as keyof typeof hintNames] || NAME // if stepNumber for some reason is not in hintNames, default to NAME

  if (stepNumber === 9) {
    const contentToneDescription = contentTone
      ? `composer.content.ai.generation.tone.${contentTone}.description`
      : `composer.content.ai.generation.tone.default.description`

    return {
      unanimatedContent: (
        <Fragment>
          <div>
            <Badge variant='info-filled' weight={600} data-testid='tip'>
              <Trans i18nKey='composer.content.ai.generation.tone.example' />
            </Badge>
          </div>

          <Spacer space={10} />
        </Fragment>
      ),
      animatedContent: <Trans i18nKey={contentToneDescription} />
    }
  }

  return {
    unanimatedContent: undefined,
    animatedContent: (
      <Trans
        i18nKey={hints[name]?.[Math.floor(Math.random() * hints[name].length)]}
      />
    )
  }
}

const getHintKey = (stepNumber: number, tone?: string) => {
  if (stepNumber === 9 && tone) {
    return tone
  }

  return `${stepNumber}`
}

const SecondColumnContentV2: React.FC<SecondColumnContentProps> = () => {
  const { stepNumber } = useSteps()
  const { selectedHeroSection, formik } = useWizardContext()
  const { values } = formik
  const { contentTone } = values

  const hintContent = useMemo(
    () => getHint(stepNumber, contentTone),
    [stepNumber, contentTone]
  )

  return (
    <Column>
      {selectedHeroSection && !stepsWithoutPreview.includes(stepNumber) && (
        <SectionPreview id={selectedHeroSection.name} />
      )}

      <Spacer space={30} />

      <Hint
        keyProp={getHintKey(stepNumber, contentTone)}
        unanimatedContent={hintContent.unanimatedContent}
      >
        {hintContent.animatedContent}
      </Hint>
    </Column>
  )
}

export default SecondColumnContentV2
